<template>
    <div class="shortcut-editor">
        <validation-observer ref="observer" v-slot="{ passes }">
            <span v-if="!updateItem" class="shortcut-display" @dblclick="updateItem = true">
                <strong>{{ item?.keys }}</strong>
                <span class="text-muted ml-2">- {{ item?.description }}</span>
                <b-badge v-if="item?.is_public" variant="info" class="ml-2">Public</b-badge>
            </span>
            <b-form v-if="updateItem" class="shortcut-edit-form" @submit.prevent="passes(onFormSubmit)" novalidate>
                <validation-provider vid="keys" v-slot="{ errors }">
                    <b-form-input
                        size="sm"
                        :state="errors[0] ? false : null"
                        id="keys"
                        name="keys"
                        v-model.trim="form.keys"
                        class="shortcut-keys"
                        placeholder="Keys" />
                </validation-provider>
                <validation-provider vid="description" v-slot="{ errors }">
                    <b-form-input
                        size="sm"
                        :state="errors[0] ? false : null"
                        id="description"
                        name="description"
                        v-model.trim="form.description"
                        class="shortcut-description"
                        placeholder="Description" />
                </validation-provider>
                <b-form-checkbox v-model="form.is_public" switch size="sm" class="shortcut-public">Public</b-form-checkbox>
                <div class="shortcut-actions">
                    <b-button size="sm" variant="warning" @click="updateItem = false">Undo</b-button>
                    <b-button size="sm" type="submit" variant="success">Update</b-button>
                    <b-button size="sm" type="button" variant="danger" @click="onDelete">Delete</b-button>
                </div>
            </b-form>
        </validation-observer>
    </div>
</template>

<script>
import FormExtension from "@/core/components/FormExtension"
import { mapActions } from "vuex"

export default FormExtension.extend({
    name: 'update-shortcut',
    props: ['item'],
    data() {
        return {
            form: new Form({
                keys: "",
                description: "",
                is_public: false,
                section: "other"
            }),
            updateItem: false
        }
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.isDirty = this.form.dirty()

                if (this.isDirty) {
                    this.dirty()
                } else {
                    this.validated()
                }
            }
        },
        item: {
            immediate: true,
            handler(val) {
                this.form.withData({
                    ...val,
                    is_public: val?.is_public ?? false,
                    section: val?.section ?? 'other'
                })
            }
        },
    },
    methods: {
        ...mapActions('keyboardshortcuts', ['updateShortcut', 'deleteShortcut']),
        onFormSubmit() {
            this.updateShortcut({
                id: this.item.id,
                data: {
                    keys: this.form.keys,
                    description: this.form.description,
                    section: this.form.section,
                    is_public: this.form.is_public
                }
            }).then(() => {
                this.notify('Shortcut updated', 'Shortcut was updated successfully')
                this.reset()
                this.$emit('updated')
            }).catch(errors => {
                if (errors.response) {
                    this.$refs.observer?.setErrors(errors.response.data)
                    this.hasErrors = true
                }
            })
        },
        onDelete() {
            this.deleteShortcut(this.item?.id).then(() => {
                this.notify('Shortcut deleted', 'Shortcut was deleted successfully')
                this.updateItem = false
                this.validated()
                this.$emit('deleted')
            })
        },
        reset() {
            this.form.reset()
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.updateItem = false
                this.hasErrors = false
                this.validated()
            })
        }
    }
})
</script>

<style scoped>
.shortcut-editor {
    width: 100%;
}

.shortcut-display {
    display: inline-flex;
    align-items: center;
    min-height: 31px;
}

.shortcut-edit-form {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    width: 100%;
}

.shortcut-keys {
    width: 5rem;
}

.shortcut-description {
    min-width: 12rem;
    width: 22rem;
}

.shortcut-public {
    margin-bottom: 0;
    white-space: nowrap;
}

.shortcut-actions {
    display: inline-flex;
    gap: 0.25rem;
    white-space: nowrap;
}
</style>
